import type { Bounds } from '@/modules/Editor/models/raster/Raster'

/** Binary mask of one label, cropped to its bounds plus one pixel of padding. */
export type MaskCrop = {
  width: number
  height: number
  originX: number
  originY: number
  data: Uint8Array
}

type LabelSource = { width: number; height: number; get: (x: number, y: number) => number }

export const cropLabel = (source: LabelSource, label: number, bounds: Bounds): MaskCrop => {
  const originX = Math.max(0, Math.floor(bounds.topLeft.x) - 1)
  const originY = Math.max(0, Math.floor(bounds.topLeft.y) - 1)
  const width = Math.min(source.width - 1, Math.ceil(bounds.bottomRight.x) + 1) - originX + 1
  const height = Math.min(source.height - 1, Math.ceil(bounds.bottomRight.y) + 1) - originY + 1
  const data = new Uint8Array(width * height)
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (source.get(originX + x, originY + y) === label) {
        data[y * width + x] = 1
      }
    }
  }
  return { width, height, originX, originY, data }
}

/** Calls `visit` with the image coordinates of every set pixel of `mask`, laid out like `crop`. */
export const forEachSetPixel = (
  crop: MaskCrop,
  mask: Uint8Array,
  visit: (x: number, y: number) => void,
): void => {
  for (let i = 0; i < mask.length; i++) {
    if (mask[i]) {
      visit((i % crop.width) + crop.originX, Math.floor(i / crop.width) + crop.originY)
    }
  }
}

export const maskBounds = (crop: MaskCrop, mask: Uint8Array): Bounds => {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  forEachSetPixel(crop, mask, (x, y) => {
    minX = Math.min(minX, x)
    minY = Math.min(minY, y)
    maxX = Math.max(maxX, x)
    maxY = Math.max(maxY, y)
  })
  return { topLeft: { x: minX, y: minY }, bottomRight: { x: maxX, y: maxY } }
}

export const sameCrop = (a: MaskCrop, b: MaskCrop): boolean =>
  a.width === b.width &&
  a.height === b.height &&
  a.originX === b.originX &&
  a.originY === b.originY &&
  a.data.every((value, i) => value === b.data[i])
